import type React from "react";
import { useState, useEffect } from "react";
import { createStyles, makeStyles } from "@mui/styles";
import { type Theme } from "@mui/material/styles";
import { FormControl, Select, MenuItem, InputLabel } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { TimePicker } from "@mui/x-date-pickers";
import { TimeService, getMomentTz } from "@dexteel/mesf-core";

const moment = getMomentTz();

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			"& .MuiFormLabel-root": {
				transform: "translate(14px, -6px) scale(0.75) ",
				backgroundColor: "#FFFF",
				transition: "none !important",
				left: -3.5,
				padding: "0 5px",
			},
			"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
				borderColor: "#3F51B5",
			},
			"& .MuiFormLabel-root.Mui-focused": {
				color: "#3F51B5",
			},
		},
		description: {
			marginBottom: theme.spacing(2),
			color: theme.palette.text.secondary,
		},
		title: {
			marginBottom: theme.spacing(1),
			fontWeight: 500,
		},
		formControl: {
			width: "100%",
			"& .MuiInputLabel-outlined": {
				transform: "translate(14px, 14px) scale(1)",
			},
			"& .MuiInputLabel-shrink": {
				transform: "translate(14px, -6px) scale(0.75)",
			},
			"& .MuiOutlinedInput-input": {
				padding: "10px 14px",
			},
			"& .MuiSelect-root": {
				backgroundColor: "#FFFF",
			},
			"& .MuiOutlinedInput-root": {
				height: "40px",
			},
			"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
				borderWidth: "1px !important",
				borderColor: "rgba(0, 0, 0, 0.23) !important",
			},
		},
		selectField: {
			display: "flex",
			alignItems: "center",
		},
		dateTimePicker: {
			width: "100%",
			"& .MuiInputAdornment-root": {
				height: "auto",
				marginTop: "0px !important",
			},
			"& .MuiButtonBase-root": {
				padding: "8px",
				marginRight: "0px",
			},
		},
	}),
);

const WEEK_DAYS = [
	"Sunday",
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
];
const MONTHS = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

interface PeriodStepProps {
	startDate: Date | null;
	selectedPeriod: string;
	onPeriodChange: (period: string) => void;
	timeValue: Date | null;
	onTimeChange: (time: Date | null) => void;
	cronExpression: string;
	onCronExpressionChange: (expression: string) => void;
}

export const PeriodStep: React.FC<PeriodStepProps> = ({
	startDate,
	selectedPeriod,
	onPeriodChange,
	timeValue,
	onTimeChange,
	cronExpression,
	onCronExpressionChange,
}) => {
	const classes = useStyles();
	const [dayOfWeek, setDayOfWeek] = useState<number>(-1);
	const [dayOfMonth, setDayOfMonth] = useState<number>(-1);
	const [month, setMonth] = useState<number>(-1);
	const [weekNumber, setWeekNumber] = useState<number>(-1);

	// Function to correctly calculate the occurrence of the day of the week in the month
	const calculateWeekNumberOfMonth = (date: Date): number => {
		const day = date.getDate();
		const dayOfWeek = date.getDay();
		const year = date.getFullYear();
		const month = date.getMonth();

		// Find the first occurrence of this day of the week in the month
		let count = 0;
		for (let i = 1; i <= 31; i++) {
			const testDate = new Date(year, month, i);

			// If we've moved to the next month, we're done
			if (testDate.getMonth() !== month) break;

			// If it's the same day of the week, increment counter
			if (testDate.getDay() === dayOfWeek) {
				count++;

				// If we've reached or passed the current day, this is the occurrence number
				if (testDate.getDate() >= day) {
					break;
				}
			}
		}

		return count;
	};

	// Initialize values based on startDate
	useEffect(() => {
		if (startDate) {
			const date = new Date(startDate);
			setDayOfWeek(date.getDay());
			setDayOfMonth(date.getDate());
			setMonth(date.getMonth());

			// Use the correct method to calculate the occurrence of the day of the week
			setWeekNumber(calculateWeekNumberOfMonth(date));
		}
	}, [startDate]);

	// Update cron expression based on selected period and time
	useEffect(() => {
		if (!timeValue) return;
		const timeWithServerTz = moment(timeValue);
		const minutes = timeWithServerTz.minutes();
		const hours = timeWithServerTz.hours();

		let newCronExpression = "";

		switch (selectedPeriod) {
			case "daily":
				newCronExpression = `${minutes} ${hours} * * *`;
				break;
			case "weekly":
				if (dayOfWeek >= 0) {
					newCronExpression = `${minutes} ${hours} * * ${dayOfWeek}`;
				}
				break;
			case "monthly":
				if (dayOfWeek >= 0 && weekNumber > 0) {
					newCronExpression = `${minutes} ${hours} * * ${dayOfWeek}#${weekNumber}`;
				}
				break;
			case "annually":
				if (dayOfMonth > 0 && month >= 0) {
					newCronExpression = `${minutes} ${hours} ${dayOfMonth} ${month + 1} *`;
				}
				break;
			case "weekdays":
				newCronExpression = `${minutes} ${hours} * * 1-5`;
				break;
			case "other":
				// Don't change the expression
				break;
		}

		if (newCronExpression && newCronExpression !== cronExpression) {
			onCronExpressionChange(newCronExpression);
		}
	}, [selectedPeriod, timeValue, dayOfWeek, weekNumber, dayOfMonth, month]);

	const getAvailablePeriods = () => {
		const periods = [
			{ value: "daily", label: "Daily" },
			{ value: "weekdays", label: "Every Weekday (Monday to Friday)" },
		];

		// Only add these options if we have a start date
		if (startDate) {
			// Validate that the calculated values are reasonable
			const validWeekNumber = weekNumber > 0 && weekNumber <= 5; // A month can have at most 5 occurrences
			const validDayOfWeek = dayOfWeek >= 0 && dayOfWeek <= 6;
			const validMonth = month >= 0 && month <= 11;
			const validDayOfMonth = dayOfMonth > 0 && dayOfMonth <= 31;

			if (validDayOfWeek) {
				periods.push({
					value: "weekly",
					label: `Weekly on ${WEEK_DAYS[dayOfWeek]}`,
				});
			}

			if (validDayOfWeek && validWeekNumber) {
				periods.push({
					value: "monthly",
					label: `Monthly on ${weekNumber}${getOrdinalSuffix(weekNumber)} ${WEEK_DAYS[dayOfWeek]}`,
				});
			}

			if (validMonth && validDayOfMonth) {
				periods.push({
					value: "annually",
					label: `Annually on ${MONTHS[month]} ${dayOfMonth}${getOrdinalSuffix(dayOfMonth)}`,
				});
			}
		}

		periods.push({ value: "other", label: "Other (Custom)" });

		return periods;
	};

	const getOrdinalSuffix = (n: number): string => {
		if (n <= 0) return "";

		const j = n % 10;
		const k = n % 100;

		if (j === 1 && k !== 11) {
			return "st";
		}
		if (j === 2 && k !== 12) {
			return "nd";
		}
		if (j === 3 && k !== 13) {
			return "rd";
		}
		return "th";
	};

	const handleTimeChange = (value: any) => {
		if (value) {
			// Always use server timezone for consistency
			const serverTimezone = TimeService.getInstance().getServerTimeZone();

			// Extract hours and minutes from the new value
			const newHours = moment(value).hours();
			const newMinutes = moment(value).minutes();

			// Create a new date with the correct hours and minutes
			const updatedTime = moment()
				.tz(serverTimezone)
				.hours(newHours)
				.minutes(newMinutes)
				.toDate();

			onTimeChange(updatedTime);
		} else {
			onTimeChange(null);
		}
	};

	return (
		<Grid2
			container
			spacing={2}
			style={{
				display: "flex",
				justifyContent: "center",
				marginBottom: 15,
				alignItems: "center",
			}}
		>
			<Grid2 size={{ lg: 4, md: 4, xs: 12 }}>
				<FormControl variant="outlined" fullWidth style={{ margin: 0 }}>
					<InputLabel id="recurrence-pattern-label" sx={{ top: "3px" }}>
						Recurrence Pattern
					</InputLabel>
					<Select
						labelId="recurrence-pattern-label"
						value={selectedPeriod}
						onChange={(e) => onPeriodChange(e.target.value as string)}
						label="Recurrence Pattern"
						sx={{
							height: "40px",
							marginTop: "3px",
							"& .MuiSelect-select": {
								padding: "10px 14px",
								display: "flex",
								alignItems: "center",
							},
						}}
					>
						{getAvailablePeriods().map((period) => (
							<MenuItem key={period.value} value={period.value}>
								{period.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid2>

			{selectedPeriod !== "other" && (
				<Grid2 size={{ lg: 4, md: 4, xs: 12 }}>
					<TimePicker
						label="Start Time"
						timezone={TimeService.getInstance().getServerTimeZone()}
						format="HH:mm"
						timeSteps={{ minutes: 1 }}
						value={moment(timeValue) as any}
						onChange={handleTimeChange}
						views={["hours", "minutes"]}
						className={classes.dateTimePicker}
						slotProps={{
							textField: {
								fullWidth: true,
								variant: "outlined",
								InputProps: {
									style: {
										height: "40px",
									},
								},
								inputProps: {
									style: {
										padding: "10px 14px",
									},
								},
							},
						}}
					/>
				</Grid2>
			)}
		</Grid2>
	);
};
